import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import "./styles.css";
import "./assets/styles.scss";
import "./assets/auth-screen.css";
import StyleClass from "primevue/styleclass";
import Tooltip from "primevue/tooltip";
import authService from "./services/AuthService";
import { useUserSettings } from "./composables/useUserSettings";
import { initBrandSwitchSync } from "./composables/useSelectedBrand";
import OktaVue from "@okta/okta-vue";
import { oktaAuth } from "./config/okta";

// import bootstrap icons
import "bootstrap-icons/font/bootstrap-icons.css";
import { stylePreset } from "./config/styles";
import { useEnvironment } from "./composables/useEnvironment";

const { isProduction, envLabel, envFaviconTint } = useEnvironment();

if (!isProduction) {
    document.title = `KHK Admin Portal (${envLabel})`;

    const link = document.querySelector('link[rel="icon"]');
    if (link) {
        fetch(link.href)
            .then(r => r.text())
            .then(svg => {
                const tinted = svg.replace(/fill="#00BAFF"/g, `fill="${envFaviconTint}"`);
                const blob = new Blob([tinted], { type: 'image/svg+xml' });
                link.href = URL.createObjectURL(blob);
            })
            .catch(err => console.error('Failed to tint favicon:', err));
    }
}

const app = createApp(App);
app.use(store);

// Reload this tab when another tab switches the admin's brand (shared session
// brand) so open tabs never mix brands (#2122).
initBrandSwitchSync();

// Initialize dark theme state on app startup
if (store.getters.isDarkTheme) {
    document.documentElement.classList.add("app-dark");
}

app.use(router);

// Initialize Okta Vue plugin for OAuth2 authentication
// This handles the redirect flow and token management
app.use(OktaVue, {
    oktaAuth,
    onAuthRequired: () => {
        router.push({ name: 'login' });
    },
    onAuthResume: () => {
        router.push({ name: 'login' });
    }
});
app.use(PrimeVue, {
    theme: {
        preset: stylePreset,
        options: {
            darkModeSelector: ".app-dark"
        }
    }
});
app.directive("styleclass", StyleClass);
app.use(ToastService);
app.use(ConfirmationService);
app.component("Toast", Toast);
app.component("ConfirmDialog", ConfirmDialog);
app.directive("tooltip", Tooltip);

// Register timezone tooltip directive
const { createTimezoneTooltipDirective } = useUserSettings();
app.directive("timezone-tooltip", createTimezoneTooltipDirective());

app.mount("#app");

// On page reload, sync Okta SDK tokens into Vuex and listen for renewals
// autoRenew in okta.js handles the actual refresh — we just keep Vuex in sync
if (authService.isAuthenticated()) {
    authService.syncTokensFromOkta();
    authService.startTokenSync();
}
